import { List, Map } from 'immutable';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';
import Switch from 'react-switch';
import classNames from 'classnames';

import Scanner, { ScannerContext } from '../../components/Scanner';
import { PromptModal } from '../../components/Modal';
import { addNotification, removeNotification } from '../../../../ducks/notifications';
import {
  claim,
  getAchievements,
  getMyAchievements,
  withdraw,
} from '../../../../ducks/achievements';
import { isMobile } from '../../../../mobile/helpers';
import { navigatePush } from '../../../../helpers';
import { update as updateApp } from '../../../../ducks/app';
import AchievementChain from '../../components/Achievement/Chain';
import AchievementModal from '../../components/Achievement/Modal';
import Content from '../../components/Content/PullToRefresh';
import Empty from '../../components/Empty';
import Flash from '../../components/Flash';
import Header from '../../components/Header';
import Loader from '../../components/Loader';
import Subheader from '../../components/Subheader';
import styles from './achievements.scss';
import withUser from '../../../../components/withUser';

const propTypes = {
  dispatch: PropTypes.func.isRequired,
  error: PropTypes.shape({}),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    stackPush: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    url: PropTypes.string.isRequired,
    params: PropTypes.shape({
      chain: PropTypes.string,
    }),
  }).isRequired,
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    shortcode: PropTypes.string.isRequired,
    pub_exhibitors: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
    })),
  }),
  userAchievements: PropTypes.shape(),
  achievements: PropTypes.shape(),
  transparent: PropTypes.bool,
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
  }),
  claimError: PropTypes.shape({
    message: PropTypes.string,
    error_description: PropTypes.string,
  }),
  claimed: PropTypes.bool,
  claiming: PropTypes.bool,
  onRequestOpenFn: PropTypes.func.isRequired,
  achievementNotificationsEnabled: PropTypes.bool,
  notificationsLoading: PropTypes.bool,
  achievementsLoading: PropTypes.bool,
  userAchievementsLoading: PropTypes.bool,
  debugMode: PropTypes.bool,
  offers: PropTypes.shape(),
  offersLoading: PropTypes.bool,
};

const errorMessages = {
  not_found: 'We could not find that achievement, did you scan the correct code?',
  not_self_achievable: 'Sorry, this achievement is no longer achievable.',
  user_limit_reached: 'You\'ve reached the limit of this achievement!',
  total_limit_reached: 'Sorry, we have reached the limit of this achievement.',
  before_time_start: 'Sorry, this achievement is not yet available.',
  after_time_end: 'Sorry, this achievement is no longer available.',
  cooldown: 'Slow down cowboy! You recently got this achievement.',
  wrong_order: 'Settle down friend. You’re not ready for this part of the journey just yet.',
};

class AchievementChains extends React.Component {
  constructor(props) {
    super(props);

    const { user, achievementNotificationsEnabled } = props;
    this.state = {
      notificationPromptOpen: isMobile() && user && typeof (achievementNotificationsEnabled) === 'undefined',
      prevAchievements: null,
      prevUserAchivements: null,
      prevEvent: null,
      chains: Map(),
      modal: null,
    };
  }

  componentDidMount() {
    this.getEventAndMyAchievements();
  }

  componentDidUpdate({ event: prevEvent }) {
    const { event } = this.props;

    if (prevEvent !== event) {
      this.getEventAndMyAchievements();
    }
  }

  getEventAndMyAchievements = (force) => {
    const { dispatch, event, user } = this.props;
    const dispatchList = [];

    if (event && event.shortcode) {
      dispatchList.push(getAchievements(event.shortcode, force));
    }

    if (user) {
      dispatchList.push(getMyAchievements(force));
    }

    return Promise.all(dispatchList.map((duck) => dispatch(duck)));
  }

  static getDerivedStateFromProps(props, state) {
    const { achievements, userAchievements, event } = props;

    // Only re-calculate if achievements or userAchievements changes
    if (achievements && event && (
      achievements !== state.prevAchievements
      || userAchievements !== state.prevUserAchivements
      || event !== state.prevEvent
    )) {
      // Extract all achievements that the user has from the UserAchievements into
      // a keyed list, so we can check all achievements against it and also show hidden ones.
      const claimedAchievements = (userAchievements && userAchievements.reduce((acc, item) => {
        const id = item?.getIn(['achievement', 'id']);
        if (!id) return acc;

        return acc.set(id,
          item.get('achievement')
            // If we have this achievement among the awarded ones, increase the counter of it,
            // or set to 1 if it is the first time we see it. This will be used to show how
            // many times someone has got a certain repeatable achievement.
            .set('count', acc.getIn([id, 'count'], 0) + 1)
            .set('earned_at', item.get('created_at')));
      }, Map())) || Map();

      // Make a keyed map and join it with claimedAchievements and filter out all other events
      const keyedAchievements = Map(achievements.map((item) => ([item.get('id'), item])))
        .concat(claimedAchievements)
        .filter((item) => item.get('event_ids', []).includes(event.id));

      // Make a map of all items parent_id
      const parentMap = keyedAchievements.reduce((acc, item, id) => {
        const parentId = item.get('parent_id', 'root');
        if (parentId) {
          acc[parentId] = [...(acc[parentId] || []), id];
        }
        return acc;
      }, {});

      // Generate the list of chains (achievements that have parent_id=null are root nodes)
      const chains = keyedAchievements
        // Get only the root achievements (start of a chain)
        .filter((item) => item && item.get('parent_id') === null)
        // Add all achievements to the chain (in the items list)
        .map((item) => {
          const items = [item];
          let parentId = item.get('id');
          let count = 0;

          while (parentMap[parentId]) {
            // Prevent infinite loops (you never know :P)
            count += 1;
            if (count > 100) break;

            // TODO: if we want more than one child, adjust here!
            const child = keyedAchievements.get(parentMap[parentId][0]);
            if (!child) {
              break;
            }

            items.push(child);
            parentId = child.get('id');
          }

          return item.set('items', List(items));
        });

      return {
        chains,
        prevAchievements: props.achievements,
        prevUserAchivements: props.userAchievements,
        prevEvent: event,
      };
    }
    return null;
  }

  closeNotificationPrompt = () => {
    const { dispatch } = this.props;
    dispatch(updateApp({ achievementNotificationsEnabled: false }));
    this.setState({
      notificationPromptOpen: false,
    });
  }

  activateNotifications = () => {
    const { dispatch, user } = this.props;
    dispatch(addNotification({
      id: `achievements-${user.id}`,
      type: 'achievements',
      userId: user.id,
    }))
      .then((success) => {
        dispatch(updateApp({ achievementNotificationsEnabled: success }));
        if (success) {
          this.setState({
            notificationPromptOpen: false,
          });
        }
      });
  }

  disableNotifications = () => {
    const { user, dispatch } = this.props;
    dispatch(removeNotification(`achievements-${user.id}`))
      .then(dispatch(updateApp({ achievementNotificationsEnabled: false })));
  }

  refresh = () => this.getEventAndMyAchievements(true);

  openAchievementModal = (achievement) => {
    this.setState({
      modal: achievement,
    });
  }

  render() {
    const {
      achievementNotificationsEnabled,
      achievements,
      achievementsLoading,
      claimError,
      claimed,
      claiming,
      debugMode,
      dispatch,
      history,
      match,
      notificationsLoading,
      offers,
      offersLoading,
      onRequestOpenFn,
      transparent,
      user,
      userAchievementsLoading,
    } = this.props;

    const { notificationPromptOpen, chains, modal } = this.state;

    const loading = achievementsLoading || userAchievementsLoading || claiming;

    const debug = debugMode && {
      award: (id) => {
        dispatch(claim(id));
      },
      withdraw: (id) => {
        dispatch(withdraw(id));
      },
    };

    return (
      <>
        <Flash
          title="Achievement unlocked"
          message={claimed ? 'Nice work, on to the next one!' : undefined}
        />
        <Flash
          title="Whoops!"
          message={claimError && (
            errorMessages[claimError.message]
            || (`Failed to claim the achivement: ${claimError?.message || claimError?.error_description || 'Unknown error'}. 
                Try again later or contact support@dreamhack.com`
            ))}
          error
        />
        <Scanner>
          <Header
            transparent={transparent}
            onMenuOpen={onRequestOpenFn}
            history={history}
            loading={loading}
            showUser={!isMobile()}
            right={(
              <ScannerContext.Consumer>
                {({ activateScanner, available, loading: cameraLoading }) => available && (
                  <div
                    className={styles.scan}
                    role="button"
                    tabIndex={0}
                    onClick={() => activateScanner()}
                  >
                    {cameraLoading
                      ? <i className="far fa-spinner-third fa-spin" />
                      : <i className="far fa-qrcode" />}
                    Scan
                  </div>
                )}
              </ScannerContext.Consumer>
            )}
          >
            Achievements
          </Header>
          <Content transparent={transparent} onRefresh={this.refresh}>
            <Loader
              loading={loading && (
                (!match.params.chain && !chains.size)
                || (match.params.chain && !chains.has(match.params.chain))
              )}
            />
            { !transparent && (
              <>
                <Empty
                  empty={!loading && chains.size === 0}
                  title="There are no achievements"
                  message="There are currently no active achievements. Come back later to see if we've opened up some new ones!"
                />
                {match.params.chain && (
                  <Empty
                    empty={!loading && !chains.has(match.params.chain)}
                    title="Cant find the quest!"
                  />
                )}
                { chains.size > 0
                && (
                  <>
                    { (isMobile() && user && user.id) && (
                      <Subheader>
                        <div className={styles.notificationControl}>
                          <div>Activate achievements notifications</div>
                          <div className={styles.switch}>
                            { notificationsLoading
                              && (
                              <i className={classNames(
                                'far fa-spinner-third',
                                [styles.rotate],
                              )}
                              />
                              )}
                            <Switch
                              checked={achievementNotificationsEnabled}
                              onChange={achievementNotificationsEnabled
                                ? () => this.disableNotifications()
                                : () => this.activateNotifications()}
                              height={12}
                              width={32}
                              handleDiameter={18}
                              uncheckedIcon={false}
                              checkedIcon={false}
                              disabled={notificationsLoading}
                              onColor="#27ae60"
                            />
                          </div>
                        </div>
                      </Subheader>
                    )}
                    <AchievementChain
                      chain={chains.first()}
                      achievements={achievements}
                      onClick={this.openAchievementModal}
                      debug={debug}
                    />
                    <AchievementModal
                      achievement={modal}
                      onRequestClose={() => this.setState({ modal: null })}
                      offers={offers}
                      offersLoading={offersLoading}
                      onClaim={() => navigatePush(history, 'me/offers')}
                    />
                  </>
                )}
              </>
            )}
          </Content>
        </Scanner>
        <PromptModal
          title="Achievement notifications"
          message="Do you want to enable notifications when you receive achievements? This will not enable any other notifications that you haven't subscribed to. You can always change your setting with the notification button up top later."
          onRequestClose={() => this.closeNotificationPrompt()}
          isOpen={notificationPromptOpen || false}
          action={{
            onClick: () => this.activateNotifications(),
            text:
              notificationsLoading
                ? (
                  <i className={classNames(
                    'far fa-spinner-third',
                    [styles.rotate],
                  )}
                  />
                )
                : <span>Activate</span>,
          }}
          buttonText="Nah, maybe later"
        />
      </>
    );
  }
}

AchievementChains.propTypes = propTypes;

function mapStateToProps(state) {
  return {
    achievementNotificationsEnabled: state.getIn(['app', 'achievementNotificationsEnabled']),
    debugMode: state.getIn(['app', 'debug']),
    transparent: state.getIn(['app', 'transparent']),

    achievements: state.getIn(['achievements', 'achievements']),
    achievementsLoading: state.getIn(['achievements', 'loading']),
    claimError: state.getIn(['achievements', 'claimError']),
    claimed: state.getIn(['achievements', 'claimed']),
    claiming: state.getIn(['achievements', 'claiming']),
    userAchievements: state.getIn(['achievements', 'userAchievements']),
    userAchievementsLoading: state.getIn(['achievements', 'loadingMe']),

    event: state.getIn(['event', 'data']),

    notificationsLoading: state.getIn(['notifications', 'loading']),

    offers: state.getIn(['offers', 'data']),
    offersLoading: state.getIn(['offers', 'loading']),
  };
}

export default withUser(connect(mapStateToProps)(AchievementChains));
